import React, { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './loginStyle.css';

/**
 * CloudPasswordChangeModal — the cloud FIRST-LOGIN / admin-reset forced password
 * change screen. Shown by LoginView when loginResult === 'changepw' (set by
 * loginCloud when /ve/login returns { changePassword:true } and withholds the token).
 *
 * The user picks a new password; onSubmit(newPassword) runs the changePasswordCloud
 * thunk (POST /ve/changepassword → re-login). It resolves { ok } | { ok:false, message };
 * on failure we show the message inline and stay open (this screen never routes to the
 * generic "Server Error" modal). On success the thunk's re-login navigates to /live.
 *
 * Styled with the login design system (.login-modal + tokens), mirroring the sibling
 * login modals — deliberately NOT the reference impl's inline styles (scalewatcher
 * PasswordChange.js), which don't match this app's house style.
 */
const CloudPasswordChangeModal = ({ onSubmit, onCancel, eviewMode = false }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const modalClass = `login-modal ${eviewMode ? 'eview-mode' : ''}`;

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (submitting) return;

    const next = newPassword.trim();
    const confirm = confirmPassword.trim();
    if (!next || !confirm) {
      setError('Please enter and confirm your new password.');
      return;
    }
    if (next !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setError('');
    setSubmitting(true);
    // The thunk resolves an outcome object; it never rejects (errors surface as ok:false).
    const result = await onSubmit(next);
    if (result && result.ok) {
      // Success → changePasswordCloud re-logs in and navigates away; leave the spinner
      // showing through the transition (this modal unmounts with the login screen).
      return;
    }
    setSubmitting(false);
    setError((result && result.message) || 'Unable to change password. Please try again.');
  };

  return (
    <div className={modalClass} data-testid="login.pwchange-modal">
      <p className="login-modal__title">Set a New Password</p>
      <p className="login-modal__text">
        Your password was reset. Choose a new password to finish signing in.
      </p>

      <form className="login-modal__form" onSubmit={handleSubmit}>
        <div className="login-modal__field">
          <label htmlFor="pwchange-new" className="login-modal__field-label">New Password:</label>
          <div className="login-modal__field-row">
            <input
              id="pwchange-new"
              className="login-modal__input"
              type={showNew ? 'text' : 'password'}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus={true}
              data-testid="login.pwchange-new"
            />
            {showNew ? (
              <FaEyeSlash className="login-field__icon" onClick={() => setShowNew(false)} />
            ) : (
              <FaEye className="login-field__icon" onClick={() => setShowNew(true)} />
            )}
          </div>
        </div>

        <div className="login-modal__field">
          <label htmlFor="pwchange-confirm" className="login-modal__field-label">Confirm Password:</label>
          <div className="login-modal__field-row">
            <input
              id="pwchange-confirm"
              className="login-modal__input"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="login.pwchange-confirm"
            />
            {showConfirm ? (
              <FaEyeSlash className="login-field__icon" onClick={() => setShowConfirm(false)} />
            ) : (
              <FaEye className="login-field__icon" onClick={() => setShowConfirm(true)} />
            )}
          </div>
        </div>

        <p className={`login-error ${error ? '' : 'login-error--hidden'}`} data-testid="login.pwchange-error">
          {error || ' '}
        </p>

        <div className="login-modal__buttons">
          <button
            type="button"
            className="modal-btn hoverable"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="modal-btn hoverable"
            disabled={submitting}
            data-testid="login.pwchange-submit"
          >
            {submitting ? 'Saving…' : 'Set Password'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CloudPasswordChangeModal;
